import React, { useEffect, useMemo, useRef, useState } from "react";
import { Player } from "../types.ts";

interface PlayerSelectionModalProps {
  players: Player[];
  onSelect: (playerId: string) => void;
  onClose: () => void;
}

/** Porovnání bez ohledu na diakritiku a velikost písmen ("pesi" najde "Peší"). */
const normalize = (text: string): string =>
  (text || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const PlayerSelectionModal: React.FC<PlayerSelectionModalProps> = ({
  players,
  onSelect,
  onClose,
}) => {
  const [query, setQuery] = useState<string>("");
  const [highlighted, setHighlighted] = useState<number>(0);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const filteredPlayers = useMemo(() => {
    const needle = normalize(query.trim());

    const matching = needle
      ? players.filter((player) =>
          normalize(player.player_name).includes(needle)
        )
      : [...players];

    // Hráči začínající na hledaný text jsou první, zbytek abecedně
    return matching.sort((a, b) => {
      if (needle) {
        const aStarts = normalize(a.player_name).startsWith(needle);
        const bStarts = normalize(b.player_name).startsWith(needle);
        if (aStarts !== bStarts) return aStarts ? -1 : 1;
      }
      return a.player_name.localeCompare(b.player_name, "cs");
    });
  }, [players, query]);

  // Po změně hledání začínáme zase od prvního výsledku
  useEffect(() => {
    setHighlighted(0);
  }, [query]);

  // Vybraná položka musí zůstat vidět i při procházení šipkami
  useEffect(() => {
    itemRefs.current[highlighted]?.scrollIntoView({ block: "nearest" });
  }, [highlighted]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlighted((index) =>
        filteredPlayers.length === 0
          ? 0
          : (index + 1) % filteredPlayers.length
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlighted((index) =>
        filteredPlayers.length === 0
          ? 0
          : (index - 1 + filteredPlayers.length) % filteredPlayers.length
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      const player = filteredPlayers[highlighted];
      if (player) onSelect(player.id);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-10 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center">
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          <div className="flex justify-between items-center pb-3">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Vyberte hráče
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Zavřít</span>
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <input
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Hledat hráče…"
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />

          <div className="mt-4 max-h-80 overflow-y-auto">
            {filteredPlayers.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {filteredPlayers.map((player, index) => (
                  <li key={player.id} className="py-1">
                    <button
                      ref={(el) => {
                        itemRefs.current[index] = el;
                      }}
                      onClick={() => onSelect(player.id)}
                      onMouseEnter={() => setHighlighted(index)}
                      className={`w-full text-left px-4 py-2 rounded-md ${
                        index === highlighted
                          ? "bg-blue-100 text-blue-800"
                          : "hover:bg-gray-100"
                      }`}
                    >
                      {player.player_name}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-center py-4">
                {players.length === 0
                  ? "Všichni dostupní hráči jsou již přiřazeni k turnaji."
                  : `Žádný hráč neodpovídá hledání „${query.trim()}“.`}
              </p>
            )}
          </div>

          <div className="mt-6 flex justify-between items-center">
            <span className="text-xs text-gray-400">
              ↑ ↓ pro pohyb, Enter vybere, Esc zavře
            </span>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 focus:outline-none"
            >
              Zrušit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerSelectionModal;
